/**
 * Packed integer chunk keys — THE single home for coordinate-key packing.
 *
 * String keys (`${cx},${cy},${cz}`) allocate + hash a string on every Map probe, which is
 * measurable in hot loops (see CLAUDE.md terrain perf invariants). These helpers pack coordinates
 * into one exact double-precision integer instead. The same math used to exist privately in four
 * places (TerrainGenerator.cellKey, LocalTerrainSource.rawChunk, LodShellSelector.packCoord, and
 * here) — byte-identical, drift waiting to happen. Every packer now lives in this module; client
 * code reaches it via the LodShellSelector re-exports (`packCoord`/`unpackCoord`) or directly.
 *
 * Range: each coordinate must be within ±65535 (±524 km of world at 8 m chunks). The packed
 * value stays below 2^51, well inside Number.MAX_SAFE_INTEGER. The lod-carrying variants trade
 * range for the lod field — see their docs.
 */
const KEY_OFFSET = 0x10000; // 65536
const KEY_STRIDE = 0x20000; // 131072
/**
 * Pack chunk coordinates into a single integer key for Map usage.
 */
export function packChunkKey(cx, cy, cz) {
    return ((cx + KEY_OFFSET) * KEY_STRIDE + (cy + KEY_OFFSET)) * KEY_STRIDE + (cz + KEY_OFFSET);
}
/**
 * Unpack a packChunkKey — the client LOD walk iterates packed in-range sets and destructures,
 * so the tuple shape is the hot-path form (`const [cx, cy, cz] = unpackCoord(p)`).
 * Exact-integer arithmetic (no Math.floor needed: the subtractions make each division exact).
 */
export function unpackChunkKeyTuple(packed) {
    const cz = (packed % KEY_STRIDE) - KEY_OFFSET;
    const rest = (packed - (cz + KEY_OFFSET)) / KEY_STRIDE;
    const cy = (rest % KEY_STRIDE) - KEY_OFFSET;
    const cx = (rest - (cy + KEY_OFFSET)) / KEY_STRIDE - KEY_OFFSET;
    return [cx, cy, cz];
}
/** 2-index packed key (same ±2^16 range per index) — for caches keyed by (i,j) pairs. */
export function packPairKey(a, b) {
    return (a + KEY_OFFSET) * KEY_STRIDE + (b + KEY_OFFSET);
}
/** Unpack a packPairKey back into its two indices. */
export function unpackPairKey(key) {
    const b = (key % KEY_STRIDE) - KEY_OFFSET;
    const a = (key - (b + KEY_OFFSET)) / KEY_STRIDE - KEY_OFFSET;
    return [a, b];
}
// ============== Lod-carrying keys ==============
/**
 * How many lod values a lod-carrying key reserves. This is the hard ceiling on the LOD onion's
 * depth: exceed it and lod N aliases the NEXT key's lod 0 — for column keys that means
 * `lodColumnInfo` hands back a neighbouring column's vertical clip (wrong sky/solid verdicts,
 * i.e. holes and stuck rings, silently). It was 8 while the deepest reachable lod was 6, which
 * the far-view ring counts now exceed.
 */
export const LOD_PACK_STRIDE = 16;
/** Deepest lod a lod-carrying packed key can represent. Everything below must fit inside it. */
export const MAX_PACKABLE_LOD = LOD_PACK_STRIDE - 1;
/**
 * Pack a COLUMN identity (cx, cz, lod) into one safe integer — the key for the per-level column
 * probes (`lodColumnInfo` / `pendingLodColumns`). Same motivation as packChunkKey: those maps are
 * read inside the LOD walk (once per node, via marginPolicyAt) and once per candidate position in
 * the ring streamer, and a `${cx},${cz},${lod}` template allocated a string on every one of them.
 * Max magnitude ≈ 2^17 · 2^17 · LOD_PACK_STRIDE ≈ 2.7e11, well inside MAX_SAFE_INTEGER
 * (packChunkKey, at ~2.3e15, is the tight one and is unaffected).
 *
 * `lod` MUST be ≤ MAX_PACKABLE_LOD — see LOD_PACK_STRIDE for what aliasing costs.
 */
export function packColumn(cx, cz, lod) {
    return ((cx + KEY_OFFSET) * KEY_STRIDE + (cz + KEY_OFFSET)) * LOD_PACK_STRIDE + lod;
}
/** Unpack a packColumn key (allocates — persistence/tooling use, never the walk). */
export function unpackColumn(key) {
    const lod = key % LOD_PACK_STRIDE;
    const [cx, cz] = unpackPairKey((key - lod) / LOD_PACK_STRIDE);
    return { cx, cz, lod };
}
// A 3-coord key has no headroom left for a ×16 lod field at the ±65535 range (2^51 · 16 > 2^53),
// so the lod-carrying chunk key uses ±32767 per axis — exactly the int16 range the wire protocol
// already imposes on chunk coordinates, so nothing representable is lost.
const LOD_KEY_OFFSET = 0x8000; // 32768
const LOD_KEY_STRIDE = 0x10000; // 65536
/**
 * Pack (cx, cy, cz, lod) into one safe integer — the key for lod-aware chunk indexes, replacing
 * parallel per-lod maps and the `chunkKey` vs `chunkKeyLod` string landmine (identical at lod 0,
 * silently disjoint above it).
 *
 * Range: each coordinate within ±32767 (int16 — the wire protocol's own bound on chunk coords),
 * `lod` ≤ MAX_PACKABLE_LOD. Max packed value 2^52 − 1, inside MAX_SAFE_INTEGER.
 */
export function packChunkKeyLod(cx, cy, cz, lod) {
    return (((cx + LOD_KEY_OFFSET) * LOD_KEY_STRIDE + (cy + LOD_KEY_OFFSET)) * LOD_KEY_STRIDE
        + (cz + LOD_KEY_OFFSET)) * LOD_PACK_STRIDE + lod;
}
/** Unpack a lod-carrying chunk key (allocates — debug/tooling use). */
export function unpackChunkKeyLod(key) {
    const lod = key % LOD_PACK_STRIDE;
    let rest = (key - lod) / LOD_PACK_STRIDE;
    const cz = (rest % LOD_KEY_STRIDE) - LOD_KEY_OFFSET;
    rest = (rest - (cz + LOD_KEY_OFFSET)) / LOD_KEY_STRIDE;
    const cy = (rest % LOD_KEY_STRIDE) - LOD_KEY_OFFSET;
    const cx = (rest - (cy + LOD_KEY_OFFSET)) / LOD_KEY_STRIDE - LOD_KEY_OFFSET;
    return { cx, cy, cz, lod };
}
/**
 * Pack a small signed voxel coordinate triple into one integer key — for grouping a MODEL's voxels
 * (a fold's cells, a per-model scan), not chunk-space positions.
 *
 * ±512 per axis, which is far past any authored model (the largest is a 12 m building at ~50 voxels a
 * side) and still inside the 30 bits a JS bitwise op keeps exact. Chunk-space keys use packChunkKey
 * and friends above; this is here so a caller reaching for `${x},${y},${z}` finds the integer instead
 * (see the packed-key invariant in docs/terrain-generation-performance.md).
 */
export function packVoxelKey(x, y, z) {
    return (((x + 512) << 20) | ((y + 512) << 10) | (z + 512)) >>> 0;
}
//# sourceMappingURL=packedKey.js.map